import { c as _c } from "react/compiler-runtime";
import { CouponStatus } from "@sticker-giant/sg-frontend-common";
import { useIsMutating } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { toSafeStrippedPath } from "@/lib/url/safePath";
import { applyCouponMutationKey, useApplyCoupon } from "@/mutations/useApplyCoupon";
import { useCart } from "@/queries/useCart";
const PROMO_PARAM = "promo";
const PROMO_STRIP_PARAMS = [PROMO_PARAM];

/**
 * Reads ?promo=X from the URL and applies it exactly once per mount, whether or not
 * the visitor already has a cart — /coupon accepts a null cart id and finds or creates
 * the customer's cart. URL is the single source of truth: on settle (success OR error)
 * the param is stripped via router.replace so a refresh cannot trigger a retry loop.
 *
 * Gating on isFetched (not !isLoading) matters: a disabled query reports isLoading
 * false, so !isLoading would fire before auth resolves and mint a guest id for a
 * visitor who is about to authenticate.
 */
export function useAutoApplyUrlPromo() {
  const $ = _c(14);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const cart = useCart();
  const applyCoupon = useApplyCoupon();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: applyCouponMutationKey
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const inFlightApplyCount = useIsMutating(t0);
  const hasFiredRef = useRef(false);
  const isMountedRef = useRef(true);
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams.get(PROMO_PARAM);
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const promo = t1;
  const isAlreadyApplied = cart.data?.couponStatus === CouponStatus.APPLIED;
  const isReady = cart.isFetched && inFlightApplyCount === 0;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      isMountedRef.current = false;
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== applyCoupon || $[6] !== isAlreadyApplied || $[7] !== isReady || $[8] !== pathname || $[9] !== promo || $[10] !== router || $[11] !== searchParams) {
    t4 = () => {
      if (hasFiredRef.current || !promo || !isReady) {
        return;
      }
      hasFiredRef.current = true;
      const stripUrl = () => {
        if (!isMountedRef.current) {
          return;
        }
        router.replace(toSafeStrippedPath(pathname, searchParams, PROMO_STRIP_PARAMS));
      };
      if (isAlreadyApplied) {
        stripUrl();
        return;
      }
      applyCoupon.mutateAsync({
        code: promo
      }).catch(_temp).finally(stripUrl);
    };
    t5 = [promo, isReady, isAlreadyApplied, applyCoupon, router, pathname, searchParams];
    $[5] = applyCoupon;
    $[6] = isAlreadyApplied;
    $[7] = isReady;
    $[8] = pathname;
    $[9] = promo;
    $[10] = router;
    $[11] = searchParams;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
}
function _temp() {}